
import React from 'react';
import { Card, CardContent } from 'components/ui/card';
import { ScrollArea } from 'components/ui/scroll-area';
import { titleCase, upperCase, formatDateOnly, formatDateTime } from 'lib/utils';
import OrderDetails from './OrderDetails';
import { PTLTrackingData, TrackingLeg } from './types';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';

interface TrackingPagePTLProps {
    data: PTLTrackingData;
}

const LegCard: React.FC<{ leg: TrackingLeg }> = ({ leg }) => {
    const departureFormatted = formatDateTime(leg.lhc_date);
    const arrivalFormatted = leg.arrival_date ? formatDateTime(leg.arrival_date) : null;
    const isInTransit = !leg.arrival_date;

    const handleVehicleClick = (vehicleNo: string) => {
        window.open(`/reports/vehicle-details?vehicleNumber=${encodeURIComponent(vehicleNo)}`, "_blank", "noopener,noreferrer");
    };

    const depsItems = [
        { label: 'Damaged', value: leg.deps_damaged },
        { label: 'Theft', value: leg.deps_theft },
        { label: 'Wet', value: leg.deps_wet },
        { label: 'Shortage', value: leg.deps_shortage },
    ];

    return (
        <div className="relative pl-8 pb-3">
            {/* Timeline Dot */}
            <div className="absolute left-[11px] top-2 w-3 h-3 bg-blue-600 rounded-full border-2 border-white ring-2 ring-blue-200 z-10 shadow-sm" />

            {/* Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="p-2.5">
                    {/* Top Row: LHC Number, Branch, Status */}
                    <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <div className="bg-black text-white px-2 py-0.5 rounded text-xs font-bold">
                                {leg.lhc_number}
                            </div>
                            <span className="text-[11px] text-gray-500">•</span>
                            <span className="text-[11px] text-gray-600 font-medium truncate">
                                {titleCase(leg.branch_name)}
                            </span>
                        </div>
                        <div className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${isInTransit
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-100 text-green-700'
                            }`}>
                            {isInTransit ? 'In Transit' : 'Arrived'}
                        </div>
                    </div>

                    {/* Route Row */}
                    <div className="flex items-start gap-3 mb-2">
                        {/* From */}
                        <div className="flex items-start gap-1.5 min-w-0 flex-1">
                            <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0 mt-1"></div>
                            <div className="flex flex-col min-w-0">
                                <span className="text-xs font-bold text-gray-900 leading-tight">
                                    {upperCase(leg.lhc_origin)}
                                </span>
                                <span className="text-xs text-gray-500 mt-0.5">
                                    {departureFormatted.date}
                                </span>
                            </div>
                        </div>

                        {/* Arrow with Distance */}
                        <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 border-blue-200 rounded flex-shrink-0 self-start mt-0.5">
                            <span className="text-xs font-semibold text-blue-700 whitespace-nowrap">
                                {leg.lhc_distance_km} km
                            </span>
                        </div>

                        {/* To */}
                        <div className="flex items-start gap-1.5 min-w-0 flex-1 justify-end text-right">
                            <div className="flex flex-col min-w-0">
                                <span className="text-xs font-bold text-gray-900 leading-tight">
                                    {upperCase(leg.lhc_destination)}
                                </span>
                                <span className="text-xs text-gray-500 mt-0.5">
                                    {arrivalFormatted ? arrivalFormatted.date : 'Expected'}
                                </span>
                            </div>
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${isInTransit ? 'bg-gray-300' : 'bg-green-500'
                                }`}></div>
                        </div>
                    </div>

                    {/* Vehicle and Driver Info Row */}
                    <div className="flex items-center gap-4 pt-2 border-t border-gray-50">
                        {/* Vehicle */}
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            <span className="text-[10px] text-gray-500 uppercase tracking-wide flex-shrink-0">Vehicle:</span>
                            <div className="flex items-center gap-1 min-w-0">
                                {leg.vehicle_no ? (
                                    <span
                                        onClick={() => handleVehicleClick(leg.vehicle_no!)}
                                        className="inline-flex items-center gap-1 text-xs font-semibold text-gray-900 underline hover:font-bold cursor-pointer truncate"
                                    >
                                        {leg.vehicle_no}
                                        <HiArrowTopRightOnSquare className="h-3.5 w-3.5 flex-shrink-0" />
                                    </span>
                                ) : (
                                    <span className="text-xs font-semibold text-gray-900 truncate">—</span>
                                )}
                                {leg.vehicle_type && (
                                    <>
                                        <span className="text-gray-300">•</span>
                                        <span className="text-xs text-gray-600 truncate">
                                            {leg.vehicle_type}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Driver */}
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            <span className="text-[10px] text-gray-500 uppercase tracking-wide flex-shrink-0">Driver:</span>
                            <div className="flex items-center gap-1 min-w-0">
                                <span className="text-xs font-semibold text-gray-900 truncate">
                                    {titleCase(leg.driver_name)}
                                </span>
                                {leg.driver_mobile && (
                                    <>
                                        <span className="text-gray-300">•</span>
                                        <span className="text-xs text-gray-600 truncate">
                                            {leg.driver_mobile}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Deps row */}
                    {!isInTransit && (
                        <div className="flex flex-wrap gap-2 pt-2 mt-2 border-t border-gray-50">
                            {depsItems.map((item) => (
                                <div
                                    key={item.label}
                                    title={`${item.label}: ${item.value ?? 0}`}
                                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-medium cursor-default ${
                                        item.value && item.value > 0
                                            ? 'bg-red-50 text-red-700 border border-red-200'
                                            : 'bg-gray-50 text-gray-600 border border-gray-200'
                                    }`}
                                >
                                    {item.label}: {item.value ?? 0}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const EmptyLegCard: React.FC = () => {
    return (
        <div className="relative pl-8 pb-3">
            {/* Timeline Dot */}
            <div className="absolute left-[11px] top-4 w-3 h-3 bg-gray-300 rounded-full border-2 border-white ring-2 ring-gray-200 z-10" />

            {/* Card */}
            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4 shadow-sm flex items-center justify-center">
                <span className="text-gray-400 italic text-sm font-medium">Pending Assignment</span>
            </div>
        </div>
    );
};

const TrackingPagePTL: React.FC<TrackingPagePTLProps> = ({ data }) => {
    const lastUpdatedFormatted = formatDateTime(data.last_updated);
    const legs = data.tracking.legs;
    const showEmptyLeg = !data.tracking.reaches_final_destination;
    const isOnHold = data.status === 'on_hold';
    const isIncident = !!data.incident;
    const isAwaitingPickup = data.status === 'awaiting_pickup';
    const stockDays = data.stock_since
        ? Math.floor((Date.now() - new Date(data.stock_since).getTime()) / (1000 * 60 * 60 * 24))
        : 0;

    return (
        <>
            {/* Header: From / To - Same as FTL */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-6 md:gap-6 mb-8">
                <div className="text-left min-w-0 flex-1">
                    <div className="text-gray-600 text-sm">From</div>
                    <div className="text-lg font-bold text-gray-900 mb-1 break-words">
                        {upperCase(data.consignor)}
                    </div>
                    <div className="text-md text-gray-700 mb-1">
                        left {titleCase(data.origin)}
                    </div>
                    <div className="text-left">
                        <div className="text-sm font-semibold text-gray-900">
                            {data.left_date ? formatDateOnly(data.left_date) : '-'}
                        </div>
                    </div>
                </div>
                <div className="text-left md:text-right min-w-0 flex-1">
                    <div className="text-gray-600 text-sm">To</div>
                    <div className="text-lg font-bold text-gray-900 mb-1 break-words">
                        {upperCase(data.consignee)}
                    </div>
                    <div className="text-md text-gray-700 mb-1">
                        arriving at {titleCase(data.destination)}
                    </div>
                    <div className="text-left md:text-right">
                        <div className="text-sm font-semibold text-gray-900">
                            {data.eta ? formatDateOnly(data.eta) : '-'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Journey Legs - Replacing Progress Bar */}
            <div className="mb-4">
                <div className="bg-gray-50/50 border border-gray-200 rounded-lg p-3">
                    <ScrollArea className="max-h-[240px] pr-2">
                        <div className="relative">
                            {/* Vertical Timeline Line */}
                            {legs.length > 0 && (
                                <div className="absolute left-[17px] top-2 bottom-2 w-[2px] bg-gradient-to-b from-blue-300 via-blue-200 to-gray-200 z-0" />
                            )}

                            <div className="space-y-0">
                                {legs.map((leg) => (
                                    <LegCard key={leg.leg_index} leg={leg} />
                                ))}
                                {showEmptyLeg && <EmptyLegCard />}
                            </div>
                        </div>
                    </ScrollArea>
                </div>
            </div>

            {/* Current Status - Same as FTL */}
            <div className={`mb-4 p-4 rounded-lg border ${isIncident
                    ? 'bg-red-50 border-red-200'
                    : (isOnHold || isAwaitingPickup)
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-blue-50 border-blue-200'
                }`}>
                <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full animate-pulse ${isIncident ? 'bg-red-500' : (isOnHold || isAwaitingPickup) ? 'bg-orange-500' : 'bg-blue-500'
                        }`}></div>
                    <span className={`text-sm font-medium ${isIncident ? 'text-red-900' : (isOnHold || isAwaitingPickup) ? 'text-orange-900' : 'text-blue-900'
                        }`}>
                        {isIncident
                            ? `${titleCase(data.incident?.incident_type || '')} occurred at ${data.incident?.address || '-'}`
                            : isAwaitingPickup
                            ? `Awaiting Pickup at ${data.current_address} since ${data.stock_since ? formatDateOnly(data.stock_since) : '-'} (${stockDays} days)`
                            : isOnHold
                            ? `Currently on hold at ${data.current_address} since ${data.stock_since ? formatDateOnly(data.stock_since) : '-'} (${stockDays} days)`
                            : `Currently in ${data.current_address}`
                        }
                    </span>
                </div>
                <div className={`text-xs mt-1 ${isIncident ? 'text-red-700' : (isOnHold || isAwaitingPickup) ? 'text-orange-700' : 'text-blue-700'
                    }`}>
                    Last updated: {isIncident
                            ? `${formatDateTime(data.incident!.incident_date).date} ${formatDateTime(data.incident!.incident_date).time}`
                            : `${lastUpdatedFormatted.date} ${lastUpdatedFormatted.time}`}
                </div>
            </div>

            {/* Order Details Section */}
            <div className="flex-1 min-h-0">
                <Card className="bg-white shadow-sm h-full flex flex-col">
                    <CardContent className="px-0 pb-6 pt-4 flex-1 min-h-0 flex flex-col">
                        {/* Header */}
                        <div className="px-6 pb-3 border-b border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-900">Order Details</h3>
                        </div>

                        {/* Content */}
                        <ScrollArea className="flex-1 min-h-0 px-6 pt-4">
                            <OrderDetails orderDetails={data.order_details} />
                        </ScrollArea>
                    </CardContent>
                </Card>
            </div>
        </>
    );
};

export default TrackingPagePTL;
